import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const AuditLogTable = ({ auditLogs, loading }) => {
  const [expandedRows, setExpandedRows] = useState([]);

  const toggleRowExpansion = (logId) => {
    if (expandedRows?.includes(logId)) {
      setExpandedRows(expandedRows?.filter(id => id !== logId));
    } else {
      setExpandedRows([...expandedRows, logId]);
    }
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'created':
        return { name: 'PlusCircle', color: 'text-green-600' };
      case 'updated':
        return { name: 'Edit', color: 'text-yellow-600' };
      case 'deleted':
        return { name: 'Trash2', color: 'text-red-600' };
      default:
        return { name: 'FileText', color: 'text-gray-600' };
    }
  };

  const getActionBadgeColor = (actionType) => {
    switch (actionType) {
      case 'created':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'updated':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'deleted':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const formatEntityType = (entityType) => {
    return entityType?.split('_')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: date?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const renderChangeDetails = (log) => {
    if (!log?.oldValues && !log?.newValues) {
      return <p className="text-sm text-muted-foreground">No detailed changes available</p>;
    }

    const changes = [];

    if (log?.oldValues && log?.newValues) {
      // Compare old and new values
      const oldKeys = Object.keys(log?.oldValues || {});
      const newKeys = Object.keys(log?.newValues || {});
      const allKeys = [...new Set([...oldKeys, ...newKeys])];

      allKeys?.forEach(key => {
        if (key === 'id' || key === 'createdAt' || key === 'updatedAt') return;
        
        const oldVal = log?.oldValues?.[key];
        const newVal = log?.newValues?.[key];

        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
          changes?.push({
            field: key,
            oldValue: oldVal,
            newValue: newVal
          });
        }
      });
    }

    if (changes?.length === 0 && log?.newValues) {
      // New record - show all fields
      Object.entries(log?.newValues)?.forEach(([key, value]) => {
        if (key !== 'id' && key !== 'createdAt' && key !== 'updatedAt') {
          changes?.push({
            field: key,
            oldValue: null,
            newValue: value
          });
        }
      });
    }

    if (changes?.length === 0 && log?.oldValues) {
      // Deleted record - show all fields
      Object.entries(log?.oldValues)?.forEach(([key, value]) => {
        if (key !== 'id' && key !== 'createdAt' && key !== 'updatedAt') {
          changes?.push({
            field: key,
            oldValue: value,
            newValue: null
          });
        }
      });
    }

    return (
      <div className="space-y-2">
        {changes?.map((change, index) => (
          <div key={index} className="flex items-start gap-3 text-sm">
            <span className="font-semibold text-foreground min-w-[120px]">
              {change?.field?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}:
            </span>
            <div className="flex-1">
              {change?.oldValue !== null && (
                <div className="text-red-600">
                  <span className="font-mono bg-red-50 px-2 py-1 rounded">
                    {typeof change?.oldValue === 'object' 
                      ? JSON.stringify(change?.oldValue) 
                      : String(change?.oldValue)}
                  </span>
                </div>
              )}
              {change?.oldValue !== null && change?.newValue !== null && (
                <Icon name="ArrowRight" size={14} className="text-muted-foreground my-1" />
              )}
              {change?.newValue !== null && (
                <div className="text-green-600">
                  <span className="font-mono bg-green-50 px-2 py-1 rounded">
                    {typeof change?.newValue === 'object' 
                      ? JSON.stringify(change?.newValue) 
                      : String(change?.newValue)}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg elevation-sm p-8 text-center">
        <Icon name="Loader2" size={32} className="animate-spin text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">Loading audit logs...</p>
      </div>
    );
  }

  if (!auditLogs || auditLogs?.length === 0) {
    return (
      <div className="bg-card rounded-lg elevation-sm p-8 text-center">
        <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-3" />
        <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
          No Audit Logs Found
        </h3>
        <p className="text-muted-foreground">
          No audit entries match your current filters. Try adjusting your search criteria.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg elevation-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="text-left p-3 md:p-4 font-heading font-semibold text-sm text-foreground">
                Timestamp
              </th>
              <th className="text-left p-3 md:p-4 font-heading font-semibold text-sm text-foreground">
                Action
              </th>
              <th className="text-left p-3 md:p-4 font-heading font-semibold text-sm text-foreground">
                Entity Type
              </th>
              <th className="text-left p-3 md:p-4 font-heading font-semibold text-sm text-foreground">
                Entity Name
              </th>
              <th className="text-left p-3 md:p-4 font-heading font-semibold text-sm text-foreground">
                User
              </th>
              <th className="text-left p-3 md:p-4 font-heading font-semibold text-sm text-foreground">
                Change Summary
              </th>
              <th className="text-center p-3 md:p-4 font-heading font-semibold text-sm text-foreground">
                Details
              </th>
            </tr>
          </thead>
          <tbody>
            {auditLogs?.map((log) => {
              const actionIcon = getActionIcon(log?.actionType);
              const timestamp = formatTimestamp(log?.timestamp);
              const isExpanded = expandedRows?.includes(log?.id);

              return (
                <React.Fragment key={log?.id}>
                  <tr className="border-b border-border hover:bg-muted/30 transition-smooth">
                    <td className="p-3 md:p-4">
                      <div className="text-sm">
                        <div className="font-semibold text-foreground">{timestamp?.date}</div>
                        <div className="text-muted-foreground">{timestamp?.time}</div>
                      </div>
                    </td>
                    <td className="p-3 md:p-4">
                      <div className="flex items-center gap-2">
                        <Icon name={actionIcon?.name} size={16} className={actionIcon?.color} />
                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${getActionBadgeColor(log?.actionType)}`}>
                          {log?.actionType?.charAt(0)?.toUpperCase() + log?.actionType?.slice(1)}
                        </span>
                      </div>
                    </td>
                    <td className="p-3 md:p-4">
                      <span className="text-sm text-foreground font-medium">
                        {formatEntityType(log?.entityType)}
                      </span>
                    </td>
                    <td className="p-3 md:p-4">
                      <span className="text-sm text-foreground">
                        {log?.entityName || 'N/A'}
                      </span>
                    </td>
                    <td className="p-3 md:p-4">
                      <div className="text-sm">
                        <div className="font-medium text-foreground">{log?.userName || 'System'}</div>
                        {log?.userEmail && (
                          <div className="text-muted-foreground text-xs">{log?.userEmail}</div>
                        )}
                      </div>
                    </td>
                    <td className="p-3 md:p-4">
                      <span className="text-sm text-muted-foreground">
                        {log?.changeSummary}
                      </span>
                    </td>
                    <td className="p-3 md:p-4 text-center">
                      <button
                        onClick={() => toggleRowExpansion(log?.id)}
                        className="inline-flex items-center justify-center p-2 rounded-md hover:bg-muted transition-smooth"
                      >
                        <Icon 
                          name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
                          size={18} 
                          className="text-muted-foreground"
                        />
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-muted/20">
                      <td colSpan="7" className="p-4 md:p-6">
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Icon name="Info" size={16} className="text-primary" />
                            <h4 className="font-heading font-semibold text-foreground">Change Details</h4>
                          </div>
                          
                          {renderChangeDetails(log)}

                          {/* Additional Metadata */}
                          <div className="mt-4 pt-4 border-t border-border grid grid-cols-1 md:grid-cols-3 gap-4">
                            {log?.ipAddress && (
                              <div className="text-sm">
                                <span className="font-semibold text-foreground">IP Address:</span>
                                <span className="ml-2 text-muted-foreground">{log?.ipAddress}</span>
                              </div>
                            )}
                            {log?.sessionId && (
                              <div className="text-sm">
                                <span className="font-semibold text-foreground">Session ID:</span>
                                <span className="ml-2 text-muted-foreground font-mono text-xs">
                                  {log?.sessionId?.substring(0, 8)}...
                                </span>
                              </div>
                            )}
                            <div className="text-sm">
                              <span className="font-semibold text-foreground">Transaction ID:</span>
                              <span className="ml-2 text-muted-foreground font-mono text-xs">
                                {log?.id?.substring(0, 8)}...
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AuditLogTable;